import { Box, Paper } from '@mui/material';
import { Button, Typography, colors, IconButton } from '@sweep-io/sweep-design';
import {
  Close,
  Info,
  CheckCircle,
  Warning,
  Prohibit,
  Notification,
} from '@sweep-io/sweep-design/dist/icons';
import { VerticalCenteredBox } from '../pages/environments/styledComponents';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { SweepNotificationVariant, NotificationTheme } from '../../reducers/notificationsReducer';
import { forwardRef } from 'react';

interface NotificationAlertProps {
  message: string;
  onClose?: () => void;
  severity?: SweepNotificationVariant;
  action?: {
    actionText: string;
    onActionClick: () => any;
  };
  details?: string;
  isVariant: boolean;
  linkUrl?: string;
  linkText?: string;
  linkOpenInNewTab?: boolean;
  onNavigate?: (url: string) => void;
  theme?: NotificationTheme;
}

const colorsMap = ({
  variant,
  theme = 'light',
}: {
  variant: SweepNotificationVariant;
  theme?: NotificationTheme;
}) => {
  // Dark theme uses dark background for all variants
  if (theme === 'dark') {
    return {
      background: colors.grey[900],
      textColor: colors.white,
    };
  }

  // Light theme (default)
  const possibilities = {
    [SweepNotificationVariant.Success]: {
      background: colors.mint[100],
      textColor: colors.grey[900],
    },
    [SweepNotificationVariant.Error]: {
      background: colors.blush[300],
      textColor: colors.grey[900],
    },
    [SweepNotificationVariant.Default]: {
      background: colors.grey[120],
      textColor: colors.grey[900],
    },
    [SweepNotificationVariant.Info]: {
      background: colors.mint[100],
      textColor: colors.grey[900],
    },
    [SweepNotificationVariant.Warning]: {
      background: colors.sun[300],
      textColor: colors.grey[900],
    },
    [SweepNotificationVariant.Loading]: {
      background: colors.grey[120],
      textColor: colors.grey[900],
    },
  };

  return possibilities[variant];
};

const renderSeverityIcon = (
  severity: SweepNotificationVariant,
  isVariant: boolean,
  color: string,
) => {
  switch (severity) {
    case SweepNotificationVariant.Success:
      return <CheckCircle color={color} />;
    case SweepNotificationVariant.Error:
      return isVariant ? <Notification color={color} /> : <Prohibit color={color} />;
    case SweepNotificationVariant.Warning:
      return <Warning color={color} />;
    case SweepNotificationVariant.Loading:
      return <CenteredCircularProgress size={19} color={color} minHeight="auto" />;
    case SweepNotificationVariant.Default:
    case SweepNotificationVariant.Info:
    default:
      return <Info color={color} />;
  }
};

/**
 * NOTE: Product wants to redesign notifications (styling, position, behavior).
 * Until we have a final definition, the theme/position props were added to support
 * the new dark theme styling for assessments while keeping existing notifications unchanged.
 */

export const NotificationAlert = forwardRef<any, NotificationAlertProps>(
  (
    {
      message,
      onClose,
      severity = SweepNotificationVariant.Success,
      action,
      details,
      isVariant,
      linkUrl,
      linkText,
      linkOpenInNewTab,
      onNavigate,
      theme = 'light',
    }: NotificationAlertProps,
    ref,
  ) => {
    const mappedColors = colorsMap({ variant: severity, theme });
    const textColor = mappedColors.textColor;
    const isDark = theme === 'dark';

    //"variant" doesn't have title and subtitle, so need to combine them.
    const shouldCombine = isVariant && typeof message === 'string' && details;
    const _message = shouldCombine ? `${message}. ${details}` : message;
    const _details = shouldCombine ? undefined : details;

    return (
      <Paper
        ref={ref}
        elevation={isDark ? 0 : 4}
        sx={{
          width: isDark ? 'auto' : isVariant ? '684px' : '480px',
          maxWidth: '100%',
          borderRadius: isDark ? '8px' : 0.5,
          padding: isDark ? '7px 12px' : 1.5,
          paddingLeft: isDark ? 1.5 : 2,
          background: mappedColors.background,
          ...(isDark && {
            border: '1px solid #DEDFE0',
            boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.1)',
          }),
        }}
      >
        <VerticalCenteredBox
          sx={{
            padding: '0',
            justifyContent: 'space-between',
          }}
        >
          <VerticalCenteredBox sx={{ gap: 1 }} flex={1}>
            <VerticalCenteredBox flex="0 0 24px" justifyContent="center">
              {renderSeverityIcon(severity, isVariant, textColor)}
            </VerticalCenteredBox>

            <Typography
              color={textColor}
              variant={isDark ? 'caption-bold' : isVariant ? 'caption' : 'caption-bold'}
            >
              {_message}
            </Typography>

            {/* For dark theme, show link inline next to message */}
            {linkUrl && isDark && (
              <Box
                sx={{
                  '& button:hover': { color: colors.white },
                  button: { color: colors.white },
                  '&:hover': {
                    button: { color: colors.white },
                  },
                }}
              >
                <Button
                  variant="flat-grey"
                  size="tiny"
                  underlined
                  onClick={() => {
                    if (linkOpenInNewTab) {
                      window.open(linkUrl, '_blank');
                    } else {
                      onNavigate?.(linkUrl);
                    }
                  }}
                >
                  {linkText || 'Open'}
                </Button>
              </Box>
            )}
          </VerticalCenteredBox>
          <VerticalCenteredBox sx={{ gap: 1 }}>
            {action && (
              <Button
                underlined={true}
                variant="flat-grey"
                size="small"
                onClick={action.onActionClick}
              >
                {action.actionText}
              </Button>
            )}

            {/* Hide close button for dark theme */}
            {!isDark && onClose && (
              <IconButton
                size="small"
                variant="flat"
                onClick={onClose}
                data-testid="notification-close-button"
              >
                <Close />
              </IconButton>
            )}
          </VerticalCenteredBox>
        </VerticalCenteredBox>
        {_details && (
          <VerticalCenteredBox ml={3}>
            <Typography ellipsis whiteSpace="normal" color={textColor} variant="caption">
              {_details}
            </Typography>
          </VerticalCenteredBox>
        )}
        {linkUrl && !isDark && (
          <VerticalCenteredBox ml={4}>
            <Button
              variant="flat-grey"
              size="tiny"
              underlined={true}
              onClick={() => {
                if (linkOpenInNewTab) {
                  window.open(linkUrl, '_blank');
                } else {
                  onNavigate?.(linkUrl);
                }
              }}
            >
              {linkText || 'here'}
            </Button>
          </VerticalCenteredBox>
        )}
      </Paper>
    );
  },
);
